import { FC, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { tournamentServer, coreService } from 'network-utils';
import { translate } from 'translations';
import { UserRole } from 'utils';
import { selectToken, selectLocale, selectRole, selectIsTournamentsGamePackageEnabled } from '@redux/slices/userSlice';
import { showAlert } from '@redux/slices/informationSlice';
import { useGetChallenges } from '@queries/core/challenges';
import {
    useGetPastTournaments,
    useGetUpcomingTournaments,
    getUpcomingTournaments,
    getCoreUpcomingTournaments,
    getPastTournaments
} from '@queries/tournaments/tournament';
import {
    useGetCoreUpcomingTournaments,
    useGetCorePastTournaments,
    getCorePastTournaments
} from '@queries/core/tournaments';
import eventBus from '@services/eventBus';
import Play from './play';

const PlayContainer: FC<{ setCurrentChallenge: Function }> = (props: { setCurrentChallenge: Function }) => {
    const { setCurrentChallenge } = props;
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const services = tournamentServer.tournamentsApi;
    const { tournamentsApi: tournamentsCoreService } = coreService;
    const tournamentUuidParam = new URLSearchParams(window.location.search).get('tournamentUuid');
    const [userSettings, setUserSettings] = useState<any>();
    const [upComingTournaments, setUpComingTournaments] = useState<tournamentServer.UpComingTournament[]>([]);
    const [pastTournaments, setPastTournaments] = useState<tournamentServer.PastTournament[]>([]);
    const token = useSelector(selectToken);
    const locale = useSelector(selectLocale);
    const role = useSelector(selectRole);
    const isTournamentPackageEnabled = useSelector(selectIsTournamentsGamePackageEnabled);
    const { data: challengesData, isLoading } = useGetChallenges(token);
    const { data: upcomingTournamentsData } = useGetUpcomingTournaments(token, isTournamentPackageEnabled);
    const { data: upcomingCoreTournamentsData } = useGetCoreUpcomingTournaments(token, isTournamentPackageEnabled);
    const { data: pastTournamentsData } = useGetPastTournaments(token, isTournamentPackageEnabled);
    const { data: pastCoreTournamentsData } = useGetCorePastTournaments(token, isTournamentPackageEnabled);
    const joinSuccessfullyMessage = translate('JoinTournamentSuccess');
    const joinErrorMessage = translate('JoinTournamentError');
    const joinErrorAdminMessage = translate('JoinTournamentAdminError');
    const currentPath = '/app/play';
    const joinTournament = async (input: { tournamentUuid: string }): Promise<{ success: boolean; error?: string }> => {
        const result = isTournamentPackageEnabled
            ? await tournamentsCoreService.sharedLink(token, input.tournamentUuid)
            : await services.joinTournament(token, input.tournamentUuid);
        return result;
    };

    const getErrorMessage = () => {
        return role === UserRole.ADMIN ? joinErrorAdminMessage : joinErrorMessage;
    };

    const { mutate } = useMutation(joinTournament, {
        onSuccess: (result: { success: boolean; error?: string }) => {
            if (result.success) {
                dispatch(showAlert({ value: joinSuccessfullyMessage, status: 'success' }));
                queryClient.invalidateQueries(getUpcomingTournaments);
                queryClient.invalidateQueries(getCoreUpcomingTournaments);
                queryClient.invalidateQueries(getPastTournaments);
                queryClient.invalidateQueries(getCorePastTournaments);
            } else {
                dispatch(showAlert({ value: getErrorMessage(), status: 'error' }));
            }
            navigate(currentPath, { replace: true });
        },
        onError: () => {
            dispatch(showAlert({ value: getErrorMessage(), status: 'error' }));
            navigate(currentPath, { replace: true });
        }
    });

    useEffect(() => {
        if (!tournamentUuidParam) return;
        mutate({
            tournamentUuid: tournamentUuidParam
        });
    }, [mutate, tournamentUuidParam]);

    useEffect(() => {
        if (upcomingTournamentsData && upcomingTournamentsData.success && !isTournamentPackageEnabled) {
            setUpComingTournaments(upcomingTournamentsData.tournaments.filter((tournament: any) => tournament.canJoin));
        } else if (upcomingCoreTournamentsData && upcomingCoreTournamentsData.success && isTournamentPackageEnabled) {
            setUpComingTournaments(
                upcomingCoreTournamentsData?.tournaments?.filter((tournament: any) => tournament.canJoin) || []
            );
        }
    }, [upcomingTournamentsData, upcomingCoreTournamentsData, isTournamentPackageEnabled]);

    useEffect(() => {
        if (pastTournamentsData && pastTournamentsData.success && !isTournamentPackageEnabled) {
            setPastTournaments(pastTournamentsData.tournaments.filter((tournament: any) => tournament.didParticipate));
        } else if (pastCoreTournamentsData && pastCoreTournamentsData.success && isTournamentPackageEnabled) {
            setPastTournaments(
                pastCoreTournamentsData.tournaments.filter((tournament: any) => tournament.didParticipate)
            );
        }
    }, [pastTournamentsData, pastCoreTournamentsData, isTournamentPackageEnabled]);

    useEffect(() => {
        const callback = (data: { type: string }) => {
            if (['reconnected', 'openTournamentChanged'].includes(data.type)) {
                queryClient.invalidateQueries([getUpcomingTournaments, token]);
                queryClient.invalidateQueries([getCoreUpcomingTournaments, token]);
            }
        };

        eventBus.on('notification', callback);

        return () => {
            eventBus.remove('notification', callback);
        };
    }, [queryClient, token]);

    return (
        <Play
            pastTournaments={pastTournaments}
            upComingTournaments={upComingTournaments}
            challenges={challengesData?.challenges}
            isLoading={isLoading || !challengesData?.challenges}
            setCurrentChallenge={setCurrentChallenge}
            locale={locale}
        />
    );
};

export default PlayContainer;
